import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useContent } from "../lib/content";
import { formatPersianDate, getImageUrl } from "../lib/api";
import SEO from "../components/SEO";
import { ArrowRight, Calendar, User, Tag } from "lucide-react";
import ImageWithSkeleton from "../components/ImageWithSkeleton";

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const { articles } = useContent();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [articles, slug]);

  if (!article) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-stone-900 mb-4">مقاله یافت نشد</h1>
        <Link to="/articles" className="inline-flex items-center gap-2 bg-amber-600 text-white font-semibold px-6 py-3 rounded-full">
          بازگشت به مقالات
        </Link>
      </section>
    );
  }

  const renderContent = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      const t = line.trim();
      if (t.startsWith("# ")) return <h1 key={i}>{t.slice(2)}</h1>;
      if (t.startsWith("## ")) return <h2 key={i}>{t.slice(3)}</h2>;
      if (t.startsWith("### ")) return <h3 key={i}>{t.slice(4)}</h3>;
      if (t.startsWith("- ")) return <li key={i}>{t.slice(2)}</li>;
      if (t === "") return <br key={i} />;
      const parts = t.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i}>
          {parts.map((p, j) =>
            p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <React.Fragment key={j}>{p}</React.Fragment>
          )}
        </p>
      );
    });
  };

  return (
    <>
      <SEO title={`${article.title} | تحسین`} description={article.excerpt} image={getImageUrl(article.cover_image)} />
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="article-detail">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-6">
          <Link to="/" className="hover:text-amber-700">خانه</Link>
          <ArrowRight className="w-3 h-3" />
          <Link to="/articles" className="hover:text-amber-700">مقالات</Link>
          <ArrowRight className="w-3 h-3" />
          <span className="text-stone-800 line-clamp-1">{article.title}</span>
        </nav>

        <header className="mb-8 text-right">
          <h1 className="text-3xl sm:text-5xl font-black text-stone-900 leading-tight mb-5">{article.title}</h1>
          <div className="flex items-center gap-5 text-sm text-stone-500">
            <span className="inline-flex items-center gap-1">
              <User className="w-4 h-4" /> {article.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {formatPersianDate(article.created_at)}
            </span>
          </div>
        </header>

        {article.cover_image && (
          <div className="relative w-full aspect-[16/9] rounded-3xl mb-10 shadow-lg overflow-hidden">
            <ImageWithSkeleton src={getImageUrl(article.cover_image)} alt={article.title} className="w-full h-full object-cover" fetchPriority="high" />
          </div>
        )}

        <div className="prose-rtl max-w-none">{renderContent(article.content)}</div>

        {article.tags && article.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-stone-200 flex items-center flex-wrap gap-2">
            <Tag className="w-4 h-4 text-stone-500" />
            {article.tags.map((t) => (
              <span key={t} className="text-xs bg-stone-100 text-stone-700 px-3 py-1 rounded-full">{t}</span>
            ))}
          </div>
        )}
      </article>
    </>
  );
}
